import { auth } from './firebase.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const isDevAuthBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  requireAuth?: boolean;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  retrying = false
): Promise<T> {
  const { method = 'GET', body, requireAuth = false, signal } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // 認証トークン取得（リトライ時は強制リフレッシュ）
  if (isDevAuthBypass) {
    headers['Authorization'] = 'Bearer dev-bypass-token';
  } else {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken(retrying);
      headers['Authorization'] = `Bearer ${token}`;
    } else if (requireAuth) {
      throw new ApiError(401, 'AUTHENTICATION_ERROR', '認証が必要です');
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    ...(body !== undefined && { body: JSON.stringify(body) }),
    ...(signal && { signal }),
  });

  if (!response.ok) {
    // トークン期限切れの場合、強制リフレッシュして1度だけリトライ
    if (response.status === 401 && !retrying && auth.currentUser) {
      return request<T>(path, options, true);
    }
    const errorData = await response.json().catch(() => ({
      error: { code: 'UNKNOWN_ERROR', message: 'エラーが発生しました' },
    }));
    throw new ApiError(
      response.status,
      errorData.error?.code ?? 'UNKNOWN_ERROR',
      errorData.error?.message ?? 'エラーが発生しました'
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json();
  return data.data as T;
}

async function requestForm<T>(path: string, formData: FormData, retrying = false): Promise<T> {
  const headers: Record<string, string> = {};

  // 認証トークン取得（リトライ時は強制リフレッシュ）
  if (isDevAuthBypass) {
    headers['Authorization'] = 'Bearer dev-bypass-token';
  } else {
    const user = auth.currentUser;
    if (user) {
      headers['Authorization'] = `Bearer ${await user.getIdToken(retrying)}`;
    } else {
      throw new ApiError(401, 'AUTHENTICATION_ERROR', '認証が必要です');
    }
  }

  // Content-Type は設定しない（ブラウザが boundary 付きで自動設定）
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    // トークン期限切れの場合、強制リフレッシュして1度だけリトライ
    if (response.status === 401 && !retrying && auth.currentUser) {
      return requestForm<T>(path, formData, true);
    }
    const errorData = await response.json().catch(() => ({
      error: { code: 'UNKNOWN_ERROR', message: 'エラーが発生しました' },
    }));
    throw new ApiError(
      response.status,
      errorData.error?.code ?? 'UNKNOWN_ERROR',
      errorData.error?.message ?? 'エラーが発生しました'
    );
  }

  const data = await response.json();
  return data.data as T;
}

export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' } as RequestOptions),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  postForm: <T>(path: string, formData: FormData) => requestForm<T>(path, formData),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
